(() => {
  if (customElements.get('gr-stock-catalog')) return;
  const normalizeSize = (value, system = '') => {
    let size = String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
    const abbreviation = size.match(/\((XXXS|XXS|XS|S|M|L|XL|XXL|XXXL|[2-6]XL)\)/) || size.match(/^\d{2}(?:[.,]\d)?\s*\/\s*(XXXS|XXS|XS|S|M|L|XL|XXL|XXXL|[2-6]XL)$/);
    if (abbreviation) size = abbreviation[1];
    const unit = size.match(/\b(EU|EUR|US|USA|BR)\b/);
    if (unit) { system = unit[1]; size = size.replace(unit[0], '').replace(/^[-:\s]+|[-:\s]+$/g, ''); }
    size = size.replace(',', '.').replace(/\s+/g, ' ');
    size = ({ '2XL': 'XXL', '3XL': 'XXXL', 'XXXXL': '4XL', 'XXXXXL': '5XL', 'XXXXXXL': '6XL' })[size] || size;
    system = ({ EUR: 'EU', USA: 'US' })[system] || system;
    return size ? (system ? `${system} ${size}` : size) : '';
  };
  const normalizeFootwearSize = (value, system, brand, preserveUnit = false) => {
    const normalized = normalizeSize(value, system);
    const unit = normalized.match(/^(EU|US|BR) /);
    let number = normalized.replace(/^(EU|US|BR) /, '');
    const half = number.match(/^(\d+)[.\s-]*1\/2$/);
    if (half) number = `${half[1]}.5`;
    if (!/^\d+(?:\.\d+)?$/.test(number)) return normalized;
    const numeric = Number(number);
    // Merchant numbering convention only: never convert between size systems.
    const sizing = preserveUnit && unit ? unit[1] : brand === 'PROTORK' ? 'BR' : numeric > 0 && numeric < 20 ? 'US' : numeric >= 20 && numeric <= 60 ? 'EU' : '';
    return sizing ? `${sizing} ${number}` : normalized;
  };
  const natural = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
  const sizeOrder = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL', '6XL'];
  const compareSizes = (a, b) => {
    const ai = sizeOrder.indexOf(a), bi = sizeOrder.indexOf(b);
    return ai >= 0 && bi >= 0 ? ai - bi : natural.compare(a, b);
  };
  class StockCatalog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-gr-labels]').textContent);
      this.form = this.querySelector('[data-gr-form]');
      this.fields = this.querySelector('[data-gr-fields]');
      this.grid = this.querySelector('[data-gr-grid]');
      this.counter = this.querySelector('[data-gr-count]');
      this.pages = this.querySelector('[data-gr-pages]');
      this.retry = this.querySelector('[data-gr-retry]');
      this.cache = new Map();
      this.initialSort = this.form.elements.sort_by.value;
      this.defaultSort = 'manual';
      const brandSort = [...this.form.elements.sort_by.options].find(option => option.value === 'manual');
      if (brandSort) brandSort.textContent = this.getAttribute('lang') === 'en' || document.documentElement.lang.startsWith('en') ? 'Brand: A-Z' : 'Marca: A-Z';
      this.form.elements.sort_by.value = this.defaultSort;
      this.pageSize = Number(this.dataset.pageSize) || 16;
      if (matchMedia('(max-width: 749px)').matches) this.querySelector('details').open = false;
      this.form.addEventListener('submit', event => event.preventDefault());
      this.form.addEventListener('change', event => {
        if (event.target.name === 'sort_by' && !this.isLocalSort(event.target.value)) this.load(true);
        else { this.updateBrands(this.form.elements.gr_brand.value); this.updateSizes(); this.apply(1, true); }
      });
      this.form.addEventListener('reset', event => {
        event.preventDefault();
        this.form.elements.gr_brand.value = '';
        this.form.elements.gr_size.value = '';
        if (this.form.elements.gr_solar) this.form.elements.gr_solar.checked = false;
        this.form.elements.sort_by.value = this.defaultSort;
        if (!this.isLocalSort(this.defaultSort)) this.load(true);
        else { this.updateBrands(''); this.updateSizes(); this.apply(1, true); }
      });
      this.retry.addEventListener('click', () => this.load(false));
      this.onPopState = () => this.ready && this.load(false);
      window.addEventListener('popstate', this.onPopState);
      this.load(false);
    }
    disconnectedCallback() {
      this.controller?.abort();
      window.removeEventListener('popstate', this.onPopState);
    }
    isLocalSort(value) { return value === 'manual' || /^(title|price|created)-(ascending|descending)$/.test(value) || value === this.loadedSort; }
    matchesFeature(record) { return !this.form.elements.gr_solar?.checked || record.solar; }
    pageUrl(page, sort) {
      const url = new URL(this.dataset.collectionUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('sort_by', sort);
      url.searchParams.set('page', page);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }
    async readPage(page, sort, signal) {
      const response = await fetch(this.pageUrl(page, sort), { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('section');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = doc.querySelector('gr-stock-catalog');
      if (!root) throw new Error('section');
      return { root, cards: [...root.querySelectorAll('[data-gr-card]')] };
    }
    async load(fromControl) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.fields.disabled = true;
      this.ready = false;
      this.retry.hidden = true;
      this.querySelector('[data-gr-results]').setAttribute('aria-busy', 'true');
      this.counter.textContent = this.labels.loading;
      const params = new URLSearchParams(location.search);
      if (!fromControl && this.form.elements.gr_solar) this.form.elements.gr_solar.checked = params.get('gr_solar') === '1';
      const sort = fromControl ? this.form.elements.sort_by.value : (params.get('sort_by') || this.defaultSort);
      const selectedBrand = fromControl ? this.form.elements.gr_brand.value : (params.get('gr_brand') || params.get('filter.p.tag') || '');
      const selectedSize = fromControl ? this.form.elements.gr_size.value : (params.get('gr_size') || this.labels.legacySizes[0] || [...params].find(([key]) => /^filter\.v\.option\..*(size|tall|taman)/.test(key))?.[1] || '');
      try {
        let records = this.cache.get(sort);
        if (!records) {
          const nativeQuery = [...params.keys()].some(key => key.startsWith('filter.'));
          const useInitial = !this.loadedSort && !nativeQuery && Number(this.dataset.currentPage) === 1 && sort === this.initialSort;
          const first = useInitial ? { root: this, cards: [...this.grid.querySelectorAll('[data-gr-card]')] } : await this.readPage(1, sort, controller.signal);
          const totalPages = Number(first.root.dataset.pages) || 1;
          const batches = [first.cards];
          let cursor = 2;
          await Promise.all(Array.from({ length: Math.min(2, totalPages - 1) }, async () => {
            while (cursor <= totalPages) {
              const page = cursor++;
              batches[page - 1] = (await this.readPage(page, sort, controller.signal)).cards;
            }
          }));
          const seen = new Set();
          records = batches.flat().filter(el => {
            if (seen.has(el.dataset.id)) return false;
            seen.add(el.dataset.id);
            return true;
          }).map((el, order) => {
            const brand = el.dataset.brand || '__unassigned';
            const variants = JSON.parse(el.querySelector('[data-gr-variants]').textContent).map(v => ({ ...v, size: this.dataset.footwear === 'true' ? normalizeFootwearSize(v.size, v.system, brand) : normalizeSize(v.size, v.system) }));
            const links = [...el.querySelectorAll('a[href*="/products/"]')].map(a => ({ el: a, base: a.getAttribute('href').split('?')[0] }));
            return { el, variants, links, order, brand, solar: el.dataset.solar === 'true', title: el.dataset.title, price: Number(el.dataset.price), created: Number(el.dataset.created) };
          });
          this.cache.set(sort, records);
        }
        if (controller.signal.aborted || !this.isConnected) return;
        this.records = records;
        this.loadedSort = sort;
        this.form.elements.sort_by.value = sort;
        const size = this.dataset.footwear === 'true' ? normalizeFootwearSize(selectedSize, '', selectedBrand, true) : normalizeSize(selectedSize);
        this.updateBrands(selectedBrand, size);
        this.updateSizes(size);
        this.ready = true;
        this.fields.disabled = false;
        this.querySelector('[data-gr-fallback]').hidden = true;
        this.apply(fromControl ? 1 : Number(params.get('gr_page')) || 1, true);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.counter.textContent = this.labels.error;
        this.retry.hidden = false;
      } finally {
        if (!controller.signal.aborted) this.querySelector('[data-gr-results]').setAttribute('aria-busy', 'false');
      }
    }
    populate(select, values, firstLabel, selected, compare) {
      select.replaceChildren(new Option(firstLabel, ''));
      [...values.keys()].sort(compare).forEach(value => {
        const label = value === '__unassigned' ? this.labels.unknownBrand : value;
        select.add(new Option(`${label} (${values.get(value)})`, value));
      });
      if (selected && !values.has(selected)) select.add(new Option(`${selected} (0)`, selected));
      select.value = selected;
    }
    updateBrands(selected, size = this.form.elements.gr_size.value) {
      const brands = new Map();
      this.records.forEach(c => { if (this.matchesFeature(c) && c.variants.some(v => v.stock && (!size || v.size === size))) brands.set(c.brand, (brands.get(c.brand) || 0) + 1); });
      this.populate(this.form.elements.gr_brand, brands, this.labels.allBrands, selected, natural.compare);
    }
    updateSizes(selected = this.form.elements.gr_size.value) {
      const sizes = new Map(), brand = this.form.elements.gr_brand.value;
      this.records.filter(c => this.matchesFeature(c) && (!brand || c.brand === brand)).forEach(c => {
        new Set(c.variants.filter(v => v.stock && v.size).map(v => v.size)).forEach(size => sizes.set(size, (sizes.get(size) || 0) + 1));
      });
      this.populate(this.form.elements.gr_size, sizes, this.labels.allSizes, selected, compareSizes);
    }
    apply(requestedPage, write) {
      const brand = this.form.elements.gr_brand.value, size = this.form.elements.gr_size.value, sort = this.form.elements.sort_by.value;
      const matches = this.records.filter(c => this.matchesFeature(c) && (!brand || c.brand === brand) && c.variants.some(v => v.stock && (!size || v.size === size)));
      matches.sort((a, b) => sort === 'manual' ? (a.brand === '__unassigned') - (b.brand === '__unassigned') || natural.compare(a.brand, b.brand) || natural.compare(a.title, b.title) : sort === 'title-ascending' ? natural.compare(a.title, b.title) : sort === 'title-descending' ? natural.compare(b.title, a.title) : sort === 'price-ascending' ? a.price - b.price : sort === 'price-descending' ? b.price - a.price : sort === 'created-ascending' ? a.created - b.created : sort === 'created-descending' ? b.created - a.created : a.order - b.order);
      const totalPages = Math.ceil(matches.length / this.pageSize), page = Math.max(1, Math.min(requestedPage, totalPages || 1));
      const fragment = document.createDocumentFragment();
      matches.slice((page - 1) * this.pageSize, page * this.pageSize).forEach(c => {
        const variant = c.variants.find(v => v.stock && (!size || v.size === size));
        c.links.forEach(link => { const url = new URL(link.base, location.origin); url.searchParams.set('variant', variant.id); link.el.setAttribute('href', url.pathname + url.search); });
        fragment.append(c.el);
      });
      this.grid.replaceChildren(fragment);
      this.counter.textContent = matches.length === 1 ? this.labels.one : this.labels.many.replace('[count]', matches.length);
      this.querySelector('[data-gr-empty]').hidden = matches.length !== 0;
      this.pages.replaceChildren();
      this.pages.hidden = totalPages <= 1;
      if (totalPages > 1) {
        this.addPageButton('\u2190', page - 1, page === 1, this.labels.previous);
        for (let i = 1; i <= totalPages; i++) this.addPageButton(String(i), i, false, String(i), i === page);
        this.addPageButton('\u2192', page + 1, page === totalPages, this.labels.next);
      }
      if (write) {
        const url = new URL(location.href);
        [...url.searchParams.keys()].filter(key => key.startsWith('filter.') || ['page', 'gr_page', 'gr_brand', 'gr_size', 'gr_solar'].includes(key)).forEach(key => url.searchParams.delete(key));
        if (brand) url.searchParams.set('gr_brand', brand);
        if (size) url.searchParams.set('gr_size', size);
        if (this.form.elements.gr_solar?.checked) url.searchParams.set('gr_solar', '1');
        url.searchParams.set('sort_by', sort);
        if (page > 1) url.searchParams.set('gr_page', page);
        history.replaceState(null, '', url);
      }
    }
    addPageButton(text, page, disabled, label, current = false) {
      const button = document.createElement('button');
      button.type = 'button'; button.textContent = text; button.disabled = disabled; button.setAttribute('aria-label', label);
      if (current) button.setAttribute('aria-current', 'page');
      button.addEventListener('click', () => { this.apply(page, true); this.counter.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); this.counter.setAttribute('tabindex', '-1'); this.counter.focus({ preventScroll: true }); });
      this.pages.append(button);
    }
  }
  customElements.define('gr-stock-catalog', StockCatalog);
})();

